import React, { useEffect, useState } from 'react';
import { FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import api from '../api';
import ZerodhaBrokerErrorAlert from '../components/ZerodhaBrokerErrorAlert';
import { getBrokerErrorMessage, isZerodhaApiKeyRejectedError } from '../utils/zerodhaBrokerErrors';

/**
 * Same-origin OAuth launcher — validates API key via backend before redirecting to Kite.
 * Avoids showing raw Zerodha JSON ("Invalid api_key") inside the popup.
 */
const ZerodhaOAuthStartPage = () => {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [lastError, setLastError] = useState(null);

  useEffect(() => {
    const notifyOpener = (payload) => {
      if (window.opener) {
        window.opener.postMessage(payload, window.location.origin);
      }
    };

    const start = async () => {
      try {
        const response = await api.get('/api/v1/brokers/zerodha/login-url');
        const loginUrl = response.data?.login_url;
        if (!loginUrl) {
          throw new Error('Login URL was not returned by the server.');
        }
        window.location.replace(loginUrl);
      } catch (error) {
        const userMessage = getBrokerErrorMessage(
          error,
          'Failed to start Zerodha login. Please try again.'
        );
        setLastError(error);
        setMessage(userMessage);
        setStatus('error');
        notifyOpener({
          type: 'ZERODHA_AUTH_ERROR',
          error: userMessage,
          code: isZerodhaApiKeyRejectedError(error) ? 'zerodha_api_key_rejected' : 'oauth_start_failed',
        });
      }
    };

    start();
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <FaSpinner className="animate-spin text-6xl text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Preparing Zerodha login…</h2>
          <p className="text-gray-600 text-sm">Checking your API key before opening Kite.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <FaExclamationTriangle className="text-5xl text-red-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-4">Cannot open Zerodha login</h2>
        <ZerodhaBrokerErrorAlert message={message} err={lastError} className="text-left" />
        <p className="text-sm text-gray-500 mt-4">You can close this window and fix the issue in TrendVector.</p>
      </div>
    </div>
  );
};

export default ZerodhaOAuthStartPage;
